// Live-proctoring signaling over HTTP polling through the main API (no socket server, so it
// runs on Vercel). Browsers poll for peers and for the one offer/answer each connection
// needs; video then flows directly between the browsers.
//
// Events: 'connect', 'disconnect', 'unauthorized', 'signal' ({ from, data }),
//   candidates see 'peer-joined' / 'peer-left' with { peerId }
//   watchers see the same with { peerId, attemptId, name, email }.

import axios from 'axios'
import { API } from './api'

const FAST_MS = 1000 // while something is happening
const SLOW_MS = 3000 // steady state
const FAST_WINDOW_MS = 15000
const MAX_FAILURES = 3

function randomPeerId() {
  const bytes = new Uint8Array(18)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

export class PollingSignal {
  constructor(ticket) {
    this.ticket = ticket
    this.peerId = randomPeerId()
    this.handlers = {}
    this.peers = new Map()
    this.fastUntil = Date.now() + FAST_WINDOW_MS
    this.failures = 0
    this.connected = false
    this.stopped = false
    this.timer = null
    this.http = axios.create({ baseURL: `${API}/api/st-diagnostic/signal`, headers: { Authorization: `Bearer ${ticket}` } })
  }

  on(event, fn) {
    (this.handlers[event] ||= []).push(fn)
    return this
  }

  fire(event, payload) {
    for (const fn of this.handlers[event] || []) {
      try { fn(payload) } catch (err) { console.warn(`signal handler for ${event} failed`, err) }
    }
  }

  wake() {
    this.fastUntil = Date.now() + FAST_WINDOW_MS
  }

  start() {
    this.poll()
    return this
  }

  async send(to, data) {
    this.wake()
    await this.http.post('/send', { peerId: this.peerId, to, data })
  }

  async poll() {
    if (this.stopped) return
    try {
      const { data } = await this.http.post('/poll', { peerId: this.peerId })
      this.failures = 0
      if (!this.connected) { this.connected = true; this.fire('connect') }
      this.syncPeers(data.peers)
      for (const m of data.messages) { this.wake(); this.fire('signal', m) }
    } catch (err) {
      const status = err?.response?.status
      if (status === 401 || status === 403) {
        this.connected = false
        this.fire('unauthorized', err?.response?.data?.error)
        return this.stop({ leave: false })
      }
      if (++this.failures >= MAX_FAILURES && this.connected) {
        this.connected = false
        this.fire('disconnect')
      }
    }
    if (!this.stopped) {
      this.timer = setTimeout(() => this.poll(), Date.now() < this.fastUntil ? FAST_MS : SLOW_MS)
    }
  }

  syncPeers(list) {
    const seen = new Set()
    for (const p of list) {
      seen.add(p.peerId)
      if (!this.peers.has(p.peerId)) {
        this.peers.set(p.peerId, p)
        this.wake()
        this.fire('peer-joined', p)
      }
    }
    for (const [id, p] of this.peers) {
      if (!seen.has(id)) {
        this.peers.delete(id)
        this.fire('peer-left', p)
      }
    }
  }

  stop({ leave = true } = {}) {
    if (this.stopped) return
    this.stopped = true
    clearTimeout(this.timer)
    if (leave) {
      // keepalive lets the request finish even if the page is closing
      fetch(`${API}/api/st-diagnostic/signal/leave`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${this.ticket}` },
        body: JSON.stringify({ peerId: this.peerId })
      }).catch(() => {})
    }
  }
}

// Resolves once ICE gathering is complete, so the offer/answer already carries every
// network candidate and a single message per side is enough (no trickle ICE to poll for).
export function waitForIceGathering(pc, timeoutMs = 4000) {
  if (pc.iceGatheringState === 'complete') return Promise.resolve()
  return new Promise(resolve => {
    const finish = () => {
      clearTimeout(timer)
      pc.removeEventListener('icegatheringstatechange', check)
      resolve()
    }
    const check = () => { if (pc.iceGatheringState === 'complete') finish() }
    const timer = setTimeout(finish, timeoutMs)
    pc.addEventListener('icegatheringstatechange', check)
  })
}
